/* eslint-disable */
import { useCallback, useEffect, useRef, useState } from 'react';
import internalLogsServices from '@/Services/internal-logs.services';
import LogViewer from './LogViewer';
import styles from './InternalLogsPage.module.scss';

const MODES = { TAIL: 'tail', SEARCH: 'search', JUMP: 'jump' };
const POLL_INTERVALS = [2, 5, 10];
const LEVELS = ['', 'ERROR', 'WARNING', 'INFO', 'DEBUG'];
const MAX_LINES = 500;

function fmtBytes(n) {
	if (n == null) return '-';
	const u = ['B', 'KB', 'MB', 'GB'];
	let i = 0;
	let v = n;
	while (v >= 1024 && i < u.length - 1) { v /= 1024; i += 1; }
	return `${v.toFixed(1)} ${u[i]}`;
}

export default function InternalLogsPage() {
	const [serverBlocks, setServerBlocks] = useState([]);
	const [suffix, setSuffix] = useState('');
	const [files, setFiles] = useState([]);
	const [file, setFile] = useState('console.log');
	const [mode, setMode] = useState(MODES.TAIL);

	const [lines, setLines] = useState([]);
	const [startOffset, setStartOffset] = useState(null);
	const [endOffset, setEndOffset] = useState(null);
	const [atStart, setAtStart] = useState(false);
	const [atEnd, setAtEnd] = useState(true);
	const [fileSize, setFileSize] = useState(0);

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');

	const [query, setQuery] = useState('');
	const [level, setLevel] = useState('');
	const [matches, setMatches] = useState([]);
	const [searchMeta, setSearchMeta] = useState(null);

	const [jumpTs, setJumpTs] = useState('');

	const [polling, setPolling] = useState(false);
	const [intervalSec, setIntervalSec] = useState(10);
	const [follow, setFollow] = useState(true);
	const [scrollTo, setScrollTo] = useState(null);
	const pollRef = useRef(null);
	const endRef = useRef(null);
	endRef.current = endOffset;
	const scrollNonce = useRef(0);

	const run = useCallback((body) => internalLogsServices.logAction(suffix, body), [suffix]);

	const applyRead = useCallback((data, opts = {}) => {
		const { append, prepend } = opts;
		setFileSize(data.fileSize ?? 0);
		setAtStart(!!data.atStart);
		setAtEnd(!!data.atEnd);
		if (append) {
			setLines((prev) => [...prev, ...(data.lines || [])]);
			setEndOffset(data.endOffset);
		} else if (prepend) {
			setLines((prev) => [...(data.lines || []), ...prev]);
			setStartOffset(data.startOffset);
		} else {
			setLines(data.lines || []);
			setStartOffset(data.startOffset);
			setEndOffset(data.endOffset);
		}
	}, []);

	// 1. load server config once
	useEffect(() => {
		let alive = true;
		(async () => {
			try {
				setLoading(true);
				const res = await internalLogsServices.fetchLogConfig();
				if (!alive) return;
				const blocks = res?.data?.configs || [];
				setServerBlocks(blocks);
				setSuffix(blocks[0]?.suffix ?? '');
			} catch (e) {
				if (alive) setError(typeof e === 'string' ? e : e?.message || 'Failed to load config');
			} finally {
				if (alive) setLoading(false);
			}
		})();
		return () => { alive = false; };
	}, []);

	// 2. load file list when server changes
	useEffect(() => {
		if (serverBlocks.length === 0) return undefined;
		let alive = true;
		(async () => {
			try {
				setLoading(true);
				const res = await run({ action: 'listFiles' });
				if (!alive) return;
				const list = res?.data?.files || [];
				setFiles(list);
				const current = res?.data?.currentFile || list[0]?.name || 'console.log';
				setFile((prev) => (list.some((f) => f.name === prev) ? prev : current));
			} catch (e) {
				if (alive) setError(typeof e === 'string' ? e : e?.message || 'Failed to list files');
			} finally {
				if (alive) setLoading(false);
			}
		})();
		return () => { alive = false; };
	}, [suffix, serverBlocks.length, run]);

	const loadTail = useCallback(async () => {
		try {
			setLoading(true);
			const res = await run({
				action: 'read', file, anchor: { type: 'eof' },
				direction: 'backward', maxLines: MAX_LINES,
			});
			applyRead(res?.data || {});
			setScrollTo(null);
		} catch (e) {
			setError(typeof e === 'string' ? e : e?.message || 'Failed to read log');
		} finally {
			setLoading(false);
		}
	}, [run, file, applyRead]);

	// 3. (re)load when file or mode changes
	useEffect(() => {
		if (serverBlocks.length === 0) return;
		setError('');
		if (mode === MODES.TAIL) {
			loadTail();
		} else {
			setLines([]);
			setMatches([]);
			setSearchMeta(null);
			setScrollTo(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mode, file, suffix, serverBlocks.length]);

	// 4. polling in tail mode
	useEffect(() => {
		if (mode !== MODES.TAIL || !polling) {
			if (pollRef.current) clearInterval(pollRef.current);
			return undefined;
		}
		pollRef.current = setInterval(async () => {
			const from = endRef.current;
			if (from == null) return;
			try {
				const res = await run({
					action: 'read', file, anchor: { type: 'offset', value: from },
					direction: 'forward', maxLines: MAX_LINES,
				});
				const data = res?.data || {};
				if (data.rotated) {
					setLines(data.lines || []);
					setStartOffset(data.startOffset);
					setEndOffset(data.endOffset);
					setFileSize(data.fileSize ?? 0);
				} else if ((data.lines || []).length) {
					applyRead(data, { append: true });
				} else {
					setFileSize(data.fileSize ?? 0);
				}
			} catch (e) {
				setError(typeof e === 'string' ? e : e?.message || 'Poll failed');
			}
		}, intervalSec * 1000);
		return () => pollRef.current && clearInterval(pollRef.current);
	}, [mode, polling, intervalSec, file, run, applyRead]);

	const loadOlder = useCallback(async () => {
		if (startOffset == null || atStart) return;
		try {
			setLoading(true);
			const res = await run({
				action: 'read', file, anchor: { type: 'offset', value: startOffset },
				direction: 'backward', maxLines: MAX_LINES,
			});
			applyRead(res?.data || {}, { prepend: true });
		} catch (e) {
			setError(typeof e === 'string' ? e : e?.message || 'Failed to load older');
		} finally {
			setLoading(false);
		}
	}, [run, file, startOffset, atStart, applyRead]);

	const loadNewer = useCallback(async () => {
		if (endOffset == null) return;
		try {
			setLoading(true);
			const res = await run({
				action: 'read', file, anchor: { type: 'offset', value: endOffset },
				direction: 'forward', maxLines: MAX_LINES,
			});
			applyRead(res?.data || {}, { append: true });
		} catch (e) {
			setError(typeof e === 'string' ? e : e?.message || 'Failed to load newer');
		} finally {
			setLoading(false);
		}
	}, [run, file, endOffset, applyRead]);

	const doSearch = useCallback(async () => {
		try {
			setLoading(true);
			setMatches([]);
			const res = await run({ action: 'search', file, query, level: level || null });
			setMatches(res?.data?.matches || []);
			setSearchMeta(res?.data || null);
		} catch (e) {
			setError(typeof e === 'string' ? e : e?.message || 'Search failed');
		} finally {
			setLoading(false);
		}
	}, [run, file, query, level]);

	const openAround = useCallback(async (offset) => {
		try {
			setLoading(true);
			setPolling(false);
			const res = await run({
				action: 'read', file, anchor: { type: 'offset', value: offset },
				direction: 'around', maxLines: MAX_LINES,
			});
			const data = res?.data || {};
			applyRead(data);
			scrollNonce.current += 1;
			setScrollTo({ offset: data.anchorOffset ?? offset, n: scrollNonce.current });
		} catch (e) {
			setError(typeof e === 'string' ? e : e?.message || 'Failed to open');
		} finally {
			setLoading(false);
		}
	}, [run, file, applyRead]);

	const doJump = useCallback(async () => {
		if (!jumpTs) return;
		try {
			setLoading(true);
			setPolling(false);
			const value = jumpTs.replace('T', ' ');
			const res = await run({
				action: 'read', file, anchor: { type: 'timestamp', value },
				direction: 'around', maxLines: MAX_LINES,
			});
			const data = res?.data || {};
			applyRead(data);
			scrollNonce.current += 1;
			setScrollTo({ offset: data.anchorOffset, n: scrollNonce.current });
		} catch (e) {
			setError(typeof e === 'string' ? e : e?.message || 'Jump failed');
		} finally {
			setLoading(false);
		}
	}, [run, file, jumpTs, applyRead]);

	const currentFileMeta = files.find((f) => f.name === file);

	return (
		<div className={styles.page}>
			<div className={styles.bar}>
				<label>
					Server
					<select value={suffix} onChange={(e) => setSuffix(e.target.value)}>
						{serverBlocks.map((b) => (
							<option key={b.type} value={b.suffix}>{b.label || b.type}</option>
						))}
					</select>
				</label>
				<label>
					File
					<select value={file} onChange={(e) => setFile(e.target.value)}>
						{files.map((f) => (
							<option key={f.name} value={f.name}>
								{f.name} ({fmtBytes(f.size)}){f.isCurrent ? ' • current' : ''}
							</option>
						))}
					</select>
				</label>
				<div className={styles.tabs}>
					{[MODES.TAIL, MODES.SEARCH, MODES.JUMP].map((m) => (
						<button
							key={m}
							type='button'
							className={`${styles.tab} ${mode === m ? styles.tabActive : ''}`}
							onClick={() => setMode(m)}
						>
							{m === MODES.TAIL ? 'Tail' : m === MODES.SEARCH ? 'Search' : 'Jump to time'}
						</button>
					))}
				</div>
				<span className={styles.stat}>
					{fmtBytes(fileSize || currentFileMeta?.size)}
					{loading ? ' • loading…' : ''}
				</span>
			</div>

			{mode === MODES.TAIL && (
				<div className={styles.bar}>
					<label>
						<input type='checkbox' checked={polling} onChange={(e) => setPolling(e.target.checked)} />
						Auto-poll
					</label>
					<label>
						Every
						<select value={intervalSec} onChange={(e) => setIntervalSec(Number(e.target.value))}>
							{POLL_INTERVALS.map((s) => <option key={s} value={s}>{s}s</option>)}
						</select>
					</label>
					<label>
						<input type='checkbox' checked={follow} onChange={(e) => setFollow(e.target.checked)} />
						Follow bottom
					</label>
					<button type='button' className={styles.tab} onClick={loadTail}>Refresh</button>
				</div>
			)}

			{mode === MODES.SEARCH && (
				<>
					<div className={styles.bar}>
						<input
							placeholder='search text…'
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							onKeyDown={(e) => e.key === 'Enter' && doSearch()}
							style={{ minWidth: 280 }}
						/>
						<label>
							Level
							<select value={level} onChange={(e) => setLevel(e.target.value)}>
								{LEVELS.map((l) => <option key={l} value={l}>{l || 'any'}</option>)}
							</select>
						</label>
						<button type='button' className={styles.tab} onClick={doSearch}>Search</button>
					</div>
					{searchMeta && (
						<div className={styles.metaLine}>
							{searchMeta.matchCount} matches • scanned {fmtBytes(searchMeta.scannedBytes)}
							{searchMeta.hasMore ? ' • more results may exist — narrow your search' : ''}
						</div>
					)}
					{matches.length > 0 && (
						<div className={styles.matchList}>
							{matches.map((m, i) => (
								<div
									key={`${m.offset}-${i}`}
									className={styles.matchRow}
									onClick={() => openAround(m.offset)}
									title={m.text}
								>
									{m.timestamp ? `[${m.timestamp}] ` : ''}{m.text}
								</div>
							))}
						</div>
					)}
				</>
			)}

			{mode === MODES.JUMP && (
				<div className={styles.bar}>
					<label>
						Go to
						<input type='datetime-local' step='1' value={jumpTs} onChange={(e) => setJumpTs(e.target.value)} />
					</label>
					<button type='button' className={styles.tab} onClick={doJump}>Jump</button>
				</div>
			)}

			{error && <div className={styles.errorBanner}>{error}</div>}

			<LogViewer
				lines={lines}
				query={mode === MODES.SEARCH ? query : ''}
				atStart={atStart}
				atEnd={atEnd && !(mode === MODES.TAIL && polling)}
				onLoadOlder={loadOlder}
				onLoadNewer={loadNewer}
				followBottom={mode === MODES.TAIL && follow}
				scrollTo={scrollTo}
			/>
		</div>
	);
}
